import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Pencil, PackagePlus, Download, AlertTriangle, Tag, ImageIcon, Bell } from "lucide-react";

export default function Inventory() {
  const [rows, setRows] = useState([]);
  const [reorder, setReorder] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {sku, variant, ...}
  const [restocking, setRestocking] = useState(null);
  const [renaming, setRenaming] = useState(null); // {sku, product_name}

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/inventory"),
      api.get("/admin/reorder-suggestions").catch(() => ({ data: [] })),
    ]).then(([r1, r2]) => {
      setRows(r1.data);
      setReorder(r2.data || []);
      setLoading(false);
    });
  };
  useEffect(load, []);

  const downloadCsv = async () => {
    const r = await api.get("/admin/exports/inventory.csv", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = "nebula-inventory.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-6 py-10" data-testid="admin-inventory">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Stock & Receipts</div>
          <h1 className="text-3xl font-bold tracking-tight mt-1">Inventory</h1>
          <p className="text-sm text-[#5E636E] mt-1">Per-variant stock, unit cost, and restock receipts. Approved orders auto-deduct stock.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setRestocking({})} className="btn-primary inline-flex items-center gap-2" data-testid="restock-new-btn">
            <PackagePlus className="h-4 w-4" /> Record restock
          </button>
          <button onClick={downloadCsv} className="btn-secondary inline-flex items-center gap-2" data-testid="export-inventory-btn">
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>
      </div>

      {reorder.length > 0 && (
        <div className="mt-6 border border-[#FCE7AB] bg-[#FFF8E6] p-4" data-testid="reorder-alerts">
          <div className="flex items-center gap-2 text-[#9A6B00]">
            <AlertTriangle className="h-4 w-4" />
            <div className="font-mono text-[11px] tracking-[0.18em] uppercase">Reorder suggestions · {reorder.length}</div>
          </div>
          <ul className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
            {reorder.map((s) => (
              <li key={`${s.sku}-${s.variant}`} className="bg-white border border-[#FCE7AB] px-3 py-2 flex items-baseline justify-between gap-3" data-testid={`reorder-row-${s.sku}-${s.variant.replace(/\s+/g, "")}`}>
                <span><b>{s.sku}</b> <span className="text-[#5E636E]">{s.variant}</span></span>
                <span className="font-mono text-[12px]">
                  {s.on_hand} on hand · sold {s.sold_30d}/30d
                  {s.days_remaining !== null && <span> · {s.days_remaining}d left</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-8 border border-[#D1D5DB] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0B0B1F] text-white">
            <tr>
              <th className="text-left px-4 py-3 font-mono text-[11px] tracking-[0.18em] uppercase">SKU</th>
              <th className="text-left px-4 py-3 font-mono text-[11px] tracking-[0.18em] uppercase">Product</th>
              <th className="text-left px-4 py-3 font-mono text-[11px] tracking-[0.18em] uppercase">Variant</th>
              <th className="text-right px-4 py-3 font-mono text-[11px] tracking-[0.18em] uppercase">Price</th>
              <th className="text-right px-4 py-3 font-mono text-[11px] tracking-[0.18em] uppercase">Cost</th>
              <th className="text-right px-4 py-3 font-mono text-[11px] tracking-[0.18em] uppercase">On hand</th>
              <th className="text-right px-4 py-3 font-mono text-[11px] tracking-[0.18em] uppercase">Low @</th>
              <th className="text-left px-4 py-3 font-mono text-[11px] tracking-[0.18em] uppercase">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="px-4 py-8 text-center text-[#5E636E]">Loading…</td></tr>}
            {!loading && rows.map((r) => {
              const low = r.stock_qty <= r.low_stock_threshold;
              const out = !r.in_stock || r.stock_qty <= 0;
              const noVariants = !!r.no_variants;
              return (
                <tr key={`${r.sku}-${r.variant}`} className="border-t border-[#E7E8EA] hover:bg-[#F7F7F8]" data-testid={`inv-row-${r.sku}-${(r.variant || "novariant").replace(/\s+/g, "")}`}>
                  <td className="px-4 py-3 font-mono text-[12px]">{r.sku}</td>
                  <td className="px-4 py-3">
                    <span>{r.product_name}</span>
                    <button
                      onClick={() => setRenaming({ sku: r.sku, product_name: r.product_name })}
                      className="ml-2 inline-flex items-center gap-1 text-[10.5px] font-mono uppercase tracking-[0.14em] text-[#5E636E] hover:text-[#003366]"
                      title="Rename product"
                      data-testid={`rename-${r.sku}`}
                    >
                      <Tag className="h-3 w-3" /> Rename
                    </button>
                    <PhotoUploader sku={r.sku} onDone={load} />
                  </td>
                  <td className="px-4 py-3 font-mono text-[12px]">
                    {noVariants ? (
                      <span className="text-[#9A6B00] font-mono text-[10.5px] uppercase tracking-[0.14em]" title="Add a variant in /admin/products to enable stock tracking">
                        No variants yet
                      </span>
                    ) : (
                      r.variant
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-[12px]">${r.price.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right font-mono text-[12px] text-[#5E636E]">${r.cost_per_unit.toFixed(2)}</td>
                  <td className={`px-4 py-3 text-right font-mono text-[13px] font-semibold ${out ? "text-[#A30414]" : low ? "text-[#9A6B00]" : "text-[#111216]"}`}>{noVariants ? "—" : r.stock_qty}</td>
                  <td className="px-4 py-3 text-right font-mono text-[12px] text-[#5E636E]">{noVariants ? "—" : r.low_stock_threshold}</td>
                  <td className="px-4 py-3">
                    {noVariants ? (
                      <span className="badge-warning">Needs variant</span>
                    ) : out ? (
                      <span className="badge-danger">Out of stock</span>
                    ) : low ? (
                      <span className="badge-warning">Low</span>
                    ) : (
                      <span className="text-[12px] text-[#0B6623]">In stock</span>
                    )}
                    {r.watchers_count > 0 && (
                      <span
                        className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FFF1B8] text-[#7A4A00] font-mono text-[10.5px] tracking-[0.08em]"
                        title={`${r.watchers_count} buyer${r.watchers_count === 1 ? "" : "s"} waiting for restock email`}
                        data-testid={`watchers-badge-${r.sku}-${(r.variant || "novariant").replace(/\s+/g, "")}`}
                      >
                        <Bell className="h-3 w-3" /> {r.watchers_count} watching
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {noVariants ? (
                      <a
                        href="/admin/products"
                        className="inline-flex items-center gap-1 text-[12px] text-[#003366] hover:underline"
                        title="Open /admin/products to add a variant"
                        data-testid={`add-variant-${r.sku}`}
                      >
                        <Pencil className="h-3 w-3" /> Add variant
                      </a>
                    ) : (
                      <button onClick={() => setEditing(r)} className="inline-flex items-center gap-1 text-[12px] text-[#003366] hover:underline" data-testid={`edit-${r.sku}-${r.variant.replace(/\s+/g, "")}`}>
                        <Pencil className="h-3 w-3" /> Edit
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && <EditModal row={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {restocking && <RestockModal initial={restocking} rows={rows} onClose={() => setRestocking(null)} onSaved={() => { setRestocking(null); load(); }} />}
      {renaming && <RenameModal target={renaming} onClose={() => setRenaming(null)} onSaved={() => { setRenaming(null); load(); }} />}
    </div>
  );
}

function PhotoUploader({ sku, onDone }) {
  const [busy, setBusy] = useState(false);
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
      toast.error("Please pick a JPG, PNG, or WebP image.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image is larger than 8 MB — please compress and try again.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post(`/admin/products/${encodeURIComponent(sku)}/upload-image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Product photo updated");
      onDone && onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <label
      className={`ml-2 inline-flex items-center gap-1 text-[10.5px] font-mono uppercase tracking-[0.14em] cursor-pointer ${busy ? "opacity-60 pointer-events-none" : "text-[#5E636E] hover:text-[#003366]"}`}
      title="Upload / replace hero image"
      data-testid={`photo-upload-${sku}`}
    >
      <ImageIcon className="h-3 w-3" /> {busy ? "Uploading…" : "Photo"}
      <input type="file" accept="image/jpeg,image/png,image/webp" onChange={onPick} className="hidden" />
    </label>
  );
}

function RenameModal({ target, onClose, onSaved }) {
  const [name, setName] = useState(target.product_name || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const clean = name.trim();
    if (clean.length < 2 || clean.length > 120) {
      toast.error("Name must be 2–120 characters.");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/admin/products/${encodeURIComponent(target.sku)}/rename`, { product_name: clean });
      toast.success("Product renamed");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="rename-modal">
      <div className="bg-white max-w-md w-full p-6 border border-[#D1D5DB]">
        <div className="label-eyebrow">Rename product</div>
        <h2 className="text-xl font-bold mt-1">{target.sku}</h2>
        <label className="block mt-4 text-sm">
          <span className="block text-[12px] text-[#5E636E] mb-1">New product name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="input w-full"
            autoFocus
            maxLength={120}
            data-testid="rename-input"
          />
        </label>
        <p className="text-[11px] font-mono text-[#5E636E] mt-2">Applies to every variant. Change is audit-logged.</p>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="btn-secondary" data-testid="rename-cancel">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary" data-testid="rename-save">{busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

function EditModal({ row, onClose, onSaved }) {
  const [stock, setStock] = useState(row.stock_qty);
  const [cost, setCost] = useState(row.cost_per_unit);
  const [price, setPrice] = useState(row.price);
  const [low, setLow] = useState(row.low_stock_threshold);
  const [inStock, setInStock] = useState(row.in_stock);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/inventory/${encodeURIComponent(row.sku)}/${encodeURIComponent(row.variant)}`, {
        stock_qty: Number(stock), cost_per_unit: Number(cost),
        price: Number(price),
        low_stock_threshold: Number(low), in_stock: !!inStock,
      });
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="edit-variant-modal">
      <div className="bg-white max-w-md w-full p-6 border border-[#D1D5DB]">
        <div className="label-eyebrow">Edit variant</div>
        <h2 className="text-xl font-bold mt-1">{row.sku} · {row.variant}</h2>
        <div className="grid grid-cols-2 gap-4 mt-4">
          <Field label="Price USD" value={price} onChange={setPrice} step="0.01" testid="edit-price" />
          <Field label="Unit cost" value={cost} onChange={setCost} step="0.01" testid="edit-cost" />
          <Field label="Stock qty" value={stock} onChange={setStock} testid="edit-stock" />
          <Field label="Low @" value={low} onChange={setLow} testid="edit-low" />
          <div className="flex items-end col-span-2">
            <label className="text-sm flex items-center gap-2" data-testid="edit-in-stock-label">
              <input type="checkbox" checked={inStock} onChange={(e) => setInStock(e.target.checked)} data-testid="edit-in-stock" />
              <span>In stock</span>
            </label>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="btn-secondary" data-testid="edit-cancel">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary" data-testid="edit-save">Save</button>
        </div>
      </div>
    </div>
  );
}

function RestockModal({ rows, onClose, onSaved }) {
  const [sku, setSku] = useState(rows[0]?.sku || "");
  const variants = rows.filter((r) => r.sku === sku);
  const [variant, setVariant] = useState(variants[0]?.variant || "");
  const [qty, setQty] = useState(10);
  const [unitCost, setUnitCost] = useState(variants[0]?.cost_per_unit || 0);
  const [lot, setLot] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const v = rows.find((r) => r.sku === sku);
    setVariant(v?.variant || "");
    setUnitCost(v?.cost_per_unit || 0);
  }, [sku, rows]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/admin/restocks", {
        sku, variant, quantity: Number(qty), unit_cost: Number(unitCost),
        lot_number: lot, notes,
      });
      toast.success(`Restocked ${qty} of ${sku} ${variant}`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Restock failed"); }
    finally { setBusy(false); }
  };

  const skuOptions = Array.from(new Set(rows.map((r) => r.sku)));
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="restock-modal">
      <div className="bg-white max-w-md w-full p-6 border border-[#D1D5DB]">
        <div className="label-eyebrow">Record restock</div>
        <h2 className="text-xl font-bold mt-1">Receive new stock</h2>
        <div className="grid grid-cols-2 gap-4 mt-4">
          <label className="col-span-1 text-sm">
            <span className="block text-[12px] text-[#5E636E] mb-1">SKU</span>
            <select value={sku} onChange={(e) => setSku(e.target.value)} className="input w-full" data-testid="restock-sku">
              {skuOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="col-span-1 text-sm">
            <span className="block text-[12px] text-[#5E636E] mb-1">Variant</span>
            <select value={variant} onChange={(e) => setVariant(e.target.value)} className="input w-full" data-testid="restock-variant">
              {variants.map((v) => <option key={v.variant} value={v.variant}>{v.variant}</option>)}
            </select>
          </label>
          <Field label="Quantity" value={qty} onChange={setQty} testid="restock-qty" />
          <Field label="Unit cost" value={unitCost} onChange={setUnitCost} step="0.01" testid="restock-unit-cost" />
          <label className="col-span-2 text-sm">
            <span className="block text-[12px] text-[#5E636E] mb-1">Lot number (optional)</span>
            <input value={lot} onChange={(e) => setLot(e.target.value)} className="input w-full" data-testid="restock-lot" />
          </label>
          <label className="col-span-2 text-sm">
            <span className="block text-[12px] text-[#5E636E] mb-1">Notes</span>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="input w-full" rows={2} data-testid="restock-notes" />
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="btn-secondary" data-testid="restock-cancel">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary" data-testid="restock-save">Record</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, step, testid }) {
  return (
    <label className="text-sm">
      <span className="block text-[12px] text-[#5E636E] mb-1">{label}</span>
      <input type="number" step={step || "1"} value={value} onChange={(e) => onChange(e.target.value)} className="input w-full" data-testid={testid} />
    </label>
  );
}
